import React, {useEffect, useMemo, useRef, useState} from "react";
import Layout from "../../../common/Layout";
import SEO from "../../../common/SEO";
import FooterOne from "../../../common/footer/FooterOne";
import axios from "axios";
import InputMask from "react-input-mask";
import IntegrationsCarousel from "../../../components/banner/integrations";
import {useTranslation} from 'react-i18next';
import RealtimeSyncWidget from "../../../components/widgets/RealtimeSyncWidget";
import ContactUs from "../../../pages/innerpages/ContactUs";
import DemoBanner from "../../../components/banner/DemoBanner";
import Demo from "../../../innerpages/Demo";
import { useLocation } from "react-router-dom";
import ContactModalButton from "../../../components/common/ContactModalButton";

// Small helper components: section header bar and full-width spacer
const BlockHeader = ({ title, description = "", bg = true }) => (
    <div id="trade-intro" className={`edu-about-area ${bg ? 'about-style-3 bg-image' : ''}`} style={{ margin: "30px 0", padding: "4rem 0" }}>
        <div className="container eduvibe-animated-shape">
            <div className="row g-5 align-items-center">
                <div className="col-lg-12">
                    <div className="section-title text-center">
                        <h3 className="title">{title}</h3>
                        {description && <p className="description mt--20" style={{ maxWidth: 900, margin: "0 auto" }}>
                            {description}
                        </p>}
                    </div>
                </div>
            </div>
        </div>
    </div>
);
const FullWidthSpacer = () => (
  <div aria-hidden="true" style={{ width: "100%", height: 60, background: "linear-gradient(90deg,#f8fbff 0%, #f3f6fb 50%, #f8fbff 100%)", margin: "0 0" }} />
);

// Gastro-like small top spacer with background and white headline
const TopSpacer = ({ title = "", bgUrl }) => (
  <div className="edu-breadcrumb-area breadcrumb-style-1 ptb--35 ptb_md--40 ptb_sm--40 bg-image" style={{ backgroundImage: `url("${bgUrl}")` }}>
    <div className="container eduvibe-animated-shape">
      <div className="row">
        <div className="col-lg-12">
          <div className="breadcrumb-inner text-start">
            <div className="page-title">
              <h3 className="title"><span style={{ color: '#fff' }}>{title}</span></h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Gastro-like two-column module block with circular image and feature list
const TradeModule = ({ preTitle, title, desc, imageUrl, consultLabel, features = [] }) => (
  <div className="edu-about-area about-style-3 edu-section-gap bg-image">
    <div className="container eduvibe-animated-shape">
      <div className="row g-5">
          <div className="col-lg-12 section-title text-start">
              {preTitle && (
                  <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                      <span className="pre-title">{preTitle}</span>
                  </div>
              )}
              <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  <h3 className="title">{title}</h3>
                  {desc && (
                      <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                          <p className="description mt--40" dangerouslySetInnerHTML={{ __html: desc }}/>
                      </div>
                  )}
              </div>
          </div>
        <div className="col-lg-6" id="delivery-module">
          <div className="inner">
            {imageUrl && (
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '5rem 0px' }}>
                <div className="thumbnail" style={{textAlign: 'right'}}>
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                      <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                          <ContactModalButton>
                              {consultLabel}
                          </ContactModalButton>
                      </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-6" id="delivery-module">
          <div className="inner">
            {features.map((f, idx) => (
              <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                <div className="edu-feature-list">
                  <div className="content">
                    <h6 className="title d-flex align-items-center">
                        <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>
                        {f.title}
                    </h6>
                    {f.desc && <p className="description">{f.desc}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="shape-dot-wrapper shape-wrapper d-xl-block d-none">
        <div className="shape-image shape-image-1"><img src="/images/shapes/shape-21.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-2"><img src="/images/shapes/shape-13-04.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-3"><img src="/images/shapes/shape-03-05.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-4"><img src="/images/shapes/shape-15-02.png" alt="Shape Thumb" /></div>
      </div>
    </div>
  </div>
);

// Alternating gastro-like row with circular image and animated circles
const AlternatingTradeRow = ({ title, desc, features = [], imageUrl, consultLabel, reverse = false }) => (
    <div className="edu-about-area about-style-3 edu-section-gap bg-image" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
    <div className="container eduvibe-animated-shape">
      <div className="row g-5 align-items-center">
        {!reverse ? (
          <>
              <div className="col-lg-12 section-title text-start">
                  <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                      <h3 className="title">{title}</h3>
                  </div>
                  {desc && (
                      <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                          <p className="description mt--40">{desc}</p>
                      </div>
                  )}
              </div>
            <div className="col-lg-6">
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '2rem 0' }}>
                <div className="thumbnail">
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                      <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                          <ContactModalButton>
                              {consultLabel}
                          </ContactModalButton>
                      </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="inner">
                <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  <ul className="mt--20" style={{ listStyle: 'none', padding: 0 }}>
                      {features.map((f, idx) => (
                          <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                              <div className="edu-feature-list">
                                  <div className="content">
                                      <h6 className="title d-flex align-items-center">
                                          <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>{f.title}
                                      </h6>
                                      {f.desc && <p className="description">{f.desc}</p>}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
              <div className="col-lg-12 section-title text-start">
                  <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                      <h3 className="title">{title}</h3>
                  </div>
                  {desc && (
                      <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                          <p className="description mt--40">{desc}</p>
                      </div>
                  )}
              </div>
            <div className="col-lg-6 order-lg-2">
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '2rem 0' }}>
                <div className="thumbnail" style={{textAlign: 'right'}}>
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                      <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                          <ContactModalButton>
                              {consultLabel}
                          </ContactModalButton>
                      </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-lg-1">
              <div className="inner">

                <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  <ul className="mt--20" style={{ listStyle: 'none', padding: 0 }}>
                      {features.map((f, idx) => (
                          <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                              <div className="edu-feature-list">
                                  <div className="content">
                                      <h6 className="title d-flex align-items-center">
                                          <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>{f.title}
                                      </h6>
                                      {f.desc && <p className="description">{f.desc}</p>}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="shape-dot-wrapper shape-wrapper d-xl-block d-none">
        <div className="shape-image shape-image-1"><img src="/images/shapes/shape-21.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-2"><img src="/images/shapes/shape-13-04.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-3"><img src="/images/shapes/shape-03-05.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-4"><img src="/images/shapes/shape-15-02.png" alt="Shape Thumb" /></div>
      </div>
    </div>
  </div>
);

const TradePageContent = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("quote"); // quote | consult | demo
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Company search (autocomplete)
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Simplified mode helpers & inline validation
  const [isPrivate, setIsPrivate] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneVal, setPhoneVal] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const emailValid = useMemo(() => {
    const v = (emailVal || "").trim();
    return v.length > 5 && v.includes("@") && v.includes(".");
  }, [emailVal]);
  const phoneValid = useMemo(() => {
    const digits = (phoneVal || "").replace(/\D/g, "");
    return digits.length >= 9;
  }, [phoneVal]);
  const [submitError, setSubmitError] = useState(null);

  // Quick choices state and refs
  const [hasStoreVal, setHasStoreVal] = useState("no");
  const webshopTypeRef = useRef(null);
  const monthlyOrdersRef = useRef(null);
  const hasStoreSelectRef = useRef(null);
  const storeCountInputRef = useRef(null);

  // Active section highlight for sticky sub-navigation
  const sectionIds = useMemo(() => ([
    "trade-hero",
    "trade-intro",
    "trade-benefits",
    "trade-modules",
    "trade-omni",
    "trade-features",
    "trade-integrations",
    "trade-use-cases",
    "trade-faq",
    "trade-cta",
  ]), []);
  const [activeId, setActiveId] = useState("trade-hero");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0.01 }
    );
    const elements = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionIds]);

  // Company search debounce and fetch
  useEffect(() => {
    if (!showModal || isPrivate) return;
    const q = companyQuery.trim();
    if (q.length < 2) {
      setCompanyResults([]);
      setCompanyError(null);
      setShowCompanyDropdown(false);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setCompanyLoading(true);
        const res = await axios.get(`https://api.happygastro.hu/api/restaurant/name/${encodeURIComponent(q)}/search`, {
          signal: controller.signal,
        });
        const list = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.results)
            ? res.data.results
            : Array.isArray(res.data?.suggestions)
              ? res.data.suggestions
              : Array.isArray(res.data)
                ? res.data
                : [];
        setCompanyResults(list);
        setCompanyError(null);
        setShowCompanyDropdown(true);
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setCompanyError('Nem sikerült a cégkeresés. Próbáld újra.');
        }
      } finally {
        setCompanyLoading(false);
      }
    }, 350);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [companyQuery, showModal, isPrivate]);

  // Expanded data blocks to lengthen the page with grouped sections
  const featureGroups = useMemo(() => {
    const arr = t('tradePage.featureGroups', { returnObjects: true });
    return Array.isArray(arr) ? arr : [];
  }, [t]);

  const groupIcons = useMemo(() => ({
    "Készlet & Raktár": "bx bx-package",
    "Beszerzés & Beszállítók": "bx bx-cart-download",
    "Árazás & Akciók": "bx bx-dollar-circle",
    "Termékkatalógus & Variánsok": "bx bx-barcode",
    "Hűség & Vásárlói program": "bx bx-gift",
    "POS & Pénztár": "bx bx-receipt",
    "Szállítás & Logisztika": "bx bx-send",
    "Visszáru & Garancia": "bx bx-reset",
    "Omnichannel szinkron": "bx bx-sync",
    "Integrációk": "bx bx-plug",
    "Riportok & BI": "bx bx-line-chart",
    "Automatizmusok & Workflow": "bx bx-bot",
    "Jogosultság & Szerepkörök": "bx bx-shield-quarter",
    "Biztonság & Megfelelés": "bx bx-lock-alt",
    "Skálázhatóság & Teljesítmény": "bx bx-up-arrow-alt",
    "Bevezetés & Támogatás": "bx bx-support"
  }), []);

  const integrationsList = useMemo(() => ([
    "Shoprenter", "WooCommerce", "Shopify", "Magento", "GLS", "Foxpost", "Wolt",
    "MPL", "Packeta", "Barion", "OTP Simple", "Stripe", "PayPal", "NAV Online Számla",
    "NTAK", "Google Analytics", "Mailchimp", "SAP (export/import)", "Excel/CSV"
  ]), []);

  const useCases = useMemo(() => {
    const arr = t('tradePage.useCases.items', { returnObjects: true });
    return Array.isArray(arr) ? arr : [];
  }, [t]);

  const faqs = useMemo(() => {
    const arr = t('tradePage.faqs.items', { returnObjects: true });
    return Array.isArray(arr) ? arr : [];
  }, [t]);

  const openModal = (type) => {
    setFormType(type);
    setShowModal(true);
    // reset company search state when opening
    setCompanyQuery("");
    setCompanyResults([]);
    setCompanyLoading(false);
    setCompanyError(null);
    setSelectedCompany(null);
    setShowCompanyDropdown(false);
    // reset simplified mode/validation state
    setIsPrivate(false);
    setEmailVal("");
    setEmailTouched(false);
    setPhoneVal("");
    setPhoneTouched(false);
    setSubmitError(null);
    setHasStoreVal("no");
  };

  const closeModal = () => {
    setShowModal(false);
    setSubmitting(false);
    setResult(null);
    // reset company search state when closing
    setCompanyQuery("");
    setCompanyResults([]);
    setCompanyLoading(false);
    setCompanyError(null);
    setSelectedCompany(null);
    setShowCompanyDropdown(false);
    // reset simplified mode/validation state
    setIsPrivate(false);
    setEmailVal("");
    setEmailTouched(false);
    setPhoneVal("");
    setPhoneTouched(false);
    setSubmitError(null);
    setHasStoreVal("no");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    const email = (formData.get("email") || "").toString().trim();
    const phone = (formData.get("phone") || "").toString().trim();
    setSubmitError(null);
    // Inline validation instead of alerts
    let invalid = false;
    if (!email.includes("@") || !email.includes(".")) {
      setEmailVal(email);
      setEmailTouched(true);
      invalid = true;
      try { document.getElementsByName("email")[0]?.focus(); } catch (e) {}
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 9) {
      setPhoneVal(phone);
      setPhoneTouched(true);
      if (!invalid) {
        try { document.getElementsByName("phone")[0]?.focus(); } catch (e) {}
      }
      invalid = true;
    }
    if (invalid) {
      setSubmitError("Kérjük, javítsd a pirossal jelölt mezőket, majd próbáld újra.");
      return;
    }

    const name = (formData.get("fullname") || "").toString().trim();
    const webshopType = (formData.get("webshopType") || "").toString();
    const hasStore = (formData.get("hasStore") || "no").toString();
    const storeCount = (formData.get("storeCount") || "0").toString();
    const plannedStores = (formData.get("plannedStores") || "").toString();
    const shopCount = (formData.get("shopCount") || "0").toString();
    const monthlyOrders = (formData.get("monthlyOrders") || "0").toString();
    const userCount = (formData.get("userCount") || "0").toString();

    const subjectBase = formType === "consult" ? "Ingyenes konzultáció igénylése" : formType === "demo" ? "Bemutató igénylése" : "Happy Trade árajánlatkérés";

    const message = `Típus: ${subjectBase}\n\n` +
      `Milyen webáruháza van: ${webshopType}\n` +
      `Van-e boltja: ${hasStore === "yes" ? "igen" : "nem"}\n` +
      `${hasStore === "yes" ? `Boltok száma: ${storeCount}\n` : ""}` +
      `${plannedStores ? `Tervezett bolt(oka)k száma: ${plannedStores}\n` : ""}` +
      `Hány webshopja van: ${shopCount}\n` +
      `Havi internetes rendelések száma: ${monthlyOrders}\n` +
      `Felhasználók száma: ${userCount}`;

    setSubmitting(true);
    try {
      const res = await axios({
        method: "post",
        url: `https://api.happygastro.org/api/contact/submit`,
        headers: { "Content-Type": "application/json" },
        data: {
          fullname: name || "",
          email,
          phone,
          company: (formData.get("company") || "").toString(),
          subject: subjectBase,
          message,
          origin: "happysolutions.hu",
          companyData: (() => {
            const entered = (formData.get("company") || "").toString();
            if (selectedCompany && typeof selectedCompany === 'object') {
              const nm = selectedCompany.name || selectedCompany.cegnev || selectedCompany.companyName || selectedCompany.nev || entered;
              const tax = selectedCompany.adoszam || selectedCompany.tax || selectedCompany.taxNumber || selectedCompany.vat || null;
              const address = selectedCompany.address || selectedCompany.cim || selectedCompany.fullAddress || null;
              const id = selectedCompany.id || selectedCompany.uuid || selectedCompany._id || null;
              return { id, nev: nm, adoszam: tax, cim: address, raw: selectedCompany };
            }
            return { nev: entered };
          })(),
        },
      });
      setResult(res.data?.code || "OK");
      form.reset();
      setEmailVal("");
      setPhoneVal("");
      setEmailTouched(false);
      setPhoneTouched(false);
      setSubmitError(null);
    } catch (e1) {
      console.error(e1);
      setSubmitError("Sajnáljuk, technikai hiba lépett fel. Kérjük, próbáld újra később, vagy vedd fel velünk a kapcsolatot.");
    } finally {
      setSubmitting(false);
    }
  };

  const { pathname } = useLocation();
  const host = (window.location.hostname || "").replace(/^www\./i, "").toLowerCase();

  const normalizedPathname = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;

  const showHeaderImage =
        host === "happytrade.hu" &&
        (normalizedPathname === "/" || normalizedPathname === "/trade-kereskedelmi-rendszer");

  return (
    <>
      <SEO title={t('tradePage.seoTitle')} description={t('tradePage.seoDesc')} url={typeof window !== 'undefined' ? window.location.href : undefined} canonical={typeof window !== 'undefined' ? window.location.href : undefined} />
        <Layout>
            {showHeaderImage ? (
            <div className="col-lg-12" style={{ marginBottom: "2rem" }}>
                <div className="inner">
                    <img src={"https://images.happysolutions.hu/Happy_Trade_Header_1920x1080.png"} alt="Happy Trade"/>
                </div>
                <div className="col-lg-12">
                    <div className="inner">
                        <div className="col-lg-12 content text-start">
                            <h1 className="title">{t('tradePage.hero.title')}</h1>
                            <p className="description" style={{fontSize: 18}}>
                                {t('tradePage.hero.subtitle')}
                            </p>
                            <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                                <button className="edu-btn" onClick={(e) => {
                                    e.preventDefault();
                                    if (window.openContactModal) window.openContactModal();
                                }}>
                                    {t('tradePage.hero.ctaDemo')} <i
                                    className="icon-arrow-right-line-right"></i>
                                </button>
                                <ContactModalButton className="edu-btn btn-secondary">
                                    {t('tradePage.hero.ctaConsult')}
                                </ContactModalButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            ) : (
            <div id="trade-hero" className="slider-area banner-style-3 bg-image" style={{
                margin: "20px 0 0 0",
                padding: "2rem 0",
                backgroundColor: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                background: 'url(https://images.happysolutions.hu/Trade-oldal-hero--1920-500.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                minHeight: 400
            }}>
                <div className="d-flex align-items-center" style={{paddingBottom: 40}}>
                    <div className="container eduvibe-animated-shape">
                        <div className="row g-5 align-items-center">
                            <div className="col-lg-12">
                                <div className="inner">
                                    <div className="content text-start" style={{color: 'white'}}>
                                        <h1 className="title" style={{color: 'white'}}>{t('tradePage.hero.title')}</h1>
                                        <p className="description" style={{fontSize: 18, color: 'white'}}>
                                            {t('tradePage.hero.subtitle')}
                                        </p>
                                        <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                                            <Demo title={t('erpPage.hero.ctaDemo')}/>
																						{/* <button className="edu-btn" onClick={(e) => {
                                                e.preventDefault();
                                                if (window.openContactModal) window.openContactModal();
                                            }}>
                                                {t('tradePage.hero.ctaDemo')} <i
                                                className="icon-arrow-right-line-right"></i>
                                            </button> */}
                                            <button className="edu-btn btn-secondary" onClick={(e) => {
                                                e.preventDefault();
                                                if (window.openContactModal) window.openContactModal();
                                            }}>
                                                {t('tradePage.hero.ctaConsult')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="banner-image">
                                    <div className="banner-main-image">
                                        {false && <img style={{borderRadius: 8}} className="img-01"
                                                       src="/images/mini-cart.jpg" alt="Happy Trade"
                                                       width="640" height="480" decoding="async" fetchpriority="high"/>
                                        }                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            <TradeModule
                preTitle=""
                title="Happy Trade"
                desc="A Happy Trade egy <b>egységes kereskedelmi rendszer</b>, amely a bolti, kitelepüléses, online és B2B értékesítést egy platformon kapcsolja össze. <b>A készlet-, rendelés- és pénzügyi folyamatok valós időben működnek együtt</b>, így minden értékesítési csatorna átláthatóan kezelhető. A Happy Trade <b>csökkenti az adminisztrációt, automatizálja a napi működést</b>, és stabil alapot ad a skálázható kereskedelemhez."
                imageUrl="https://images.happysolutions.hu/Happy_Trade_Header_1920x1080.png"
                consultLabel={t('tradePage.hero.ctaConsult')}
                features={[
                    {
                        title: "Készlet & raktár",
                        desc: "Valós idejű készletmozgások, raktárközi átvezetések és pontos terméknyilvántartás segítik a készlethiány és a túlzott raktárkészlet elkerülését. A rendszer automatikusan követi a be- és kitárolásokat, így mindig naprakész adatok alapján hozhat döntést."},
                    {
                        title: "Pénztár & számlázás",
                        desc: "Gyors és egyszerű értékesítési folyamat pénztári és back-office környezetben, NAV-kompatibilis számlázással. Számlázz.hu és Billingo integrációval az adminisztráció automatizált és hibamentes adatkapcsolaton keresztül történik."
                    },
                    {
                        title: "Webshop szinkron",
                        desc: "Shoprenter, WooCommerce és Shopify integrációval az árak és készletek automatikusan frissülnek az online és offline csatornák között. Ez biztosítja az egységes vásárlói élményt és megszünteti a manuális adat kezelésből fakadó hibákat."
                    },
                    {
                        title: "Riportok & BI",
                        desc: "Valós idejű vezetői dashboardok és részletes kimutatások segítik az árrés, forgalom és termék teljesítmény folyamatos elemzését. A BI funkciók támogatják a gyors, adatvezérelt döntéshozatalt és az üzleti növekedést.."
                    }
                ]}
            />

            {/* Intro */}
            {/*<div id="trade-intro" className="edu-about-area about-style-3 bg-image"*/}
            {/*     style={{margin: "30px 0", padding: "4rem 0"}}>*/}
            {/*    <div className="container eduvibe-animated-shape">*/}
            {/*        <div className="row g-5 align-items-center">*/}
            {/*            <div className="col-lg-12">*/}
            {/*                <div className="section-title text-center">*/}
            {/*                    <h3 className="title">{t('tradePage.intro.title')}</h3>*/}
            {/*                    <p className="description mt--20" style={{maxWidth: 900, margin: "0 auto"}}>*/}
            {/*                        {t('tradePage.intro.desc')}*/}
            {/*                    </p>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Grouped feature sections (alternating rows) */}
            <div id="trade-features">
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.0.title')}
                    desc={t('tradePage.featureGroups.0.desc')}
                    features={t('tradePage.featureGroups.0.features')}
                    imageUrl="https://images.happysolutions.hu/raktar-es-logisztika.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                    reverse
                />
                {/*<FullWidthSpacer/>*/}
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.1.title')}
                    desc={t('tradePage.featureGroups.1.desc')}
                    features={t('tradePage.featureGroups.1.features')}
                    imageUrl="https://images.happysolutions.hu/beszerze%CC%81s-melletti-ke%CC%81p.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                />
            </div>

            {/* Grouped feature sections (alternating rows) */}
            {/*<BlockHeader title={t('tradePage.moreSections.more2')}/>*/}
            <div id="trade-features">
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.2.title')}
                    desc={t('tradePage.featureGroups.2.desc')}
                    features={t('tradePage.featureGroups.2.features')}
                    imageUrl="https://images.happysolutions.hu/a%CC%81raza%CC%81s.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                    reverse
                />
                {/*<FullWidthSpacer/>*/}
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.3.title')}
                    desc={t('tradePage.featureGroups.3.desc')}
                    features={t('tradePage.featureGroups.3.features')}
                    imageUrl="https://images.happysolutions.hu/termekkatalogus.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                />
            </div>
            <div id="trade-features">
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.4.title')}
                    desc={t('tradePage.featureGroups.4.desc')}
                    features={t('tradePage.featureGroups.4.features')}
                    imageUrl="https://images.happysolutions.hu/huseg-vasarloi-program.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                    reverse
                />
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.5.title')}
                    desc={t('tradePage.featureGroups.5.desc')}
                    features={t('tradePage.featureGroups.5.features')}
                    imageUrl="https://images.happysolutions.hu/pos-pe%CC%81nzta%CC%81r.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                />
            </div>
            <div id="trade-features">
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.6.title')}
                    desc={t('tradePage.featureGroups.6.desc')}
                    features={t('tradePage.featureGroups.6.features')}
                    imageUrl="https://images.happysolutions.hu/szallitas-logisztika.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                    reverse
                />
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.7.title')}
                    desc={t('tradePage.featureGroups.7.desc')}
                    features={t('tradePage.featureGroups.7.features')}
                    imageUrl="https://images.happysolutions.hu/reports.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                />
            </div>
            <div id="trade-features">
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.8.title')}
                    desc={t('tradePage.featureGroups.8.desc')}
                    features={t('tradePage.featureGroups.8.features')}
                    imageUrl="https://images.happysolutions.hu/automatizmusok.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                    reverse
                />
                <AlternatingTradeRow
                    title={t('tradePage.featureGroups.9.title')}
                    desc={t('tradePage.featureGroups.9.desc')}
                    features={t('tradePage.featureGroups.9.features')}
                    imageUrl="https://images.happysolutions.hu/biztonsa%CC%81g.jpg"
                    consultLabel={t('tradePage.hero.ctaConsult')}
                />
            </div>

            {/* Benefits */}
            <BlockHeader bg={false} title={t('tradePage.benefits.title')}
                         description={t('tradePage.benefits.tagline')}/>
            <section
                id="trade-benefits"
                className="edu-about-area bg-color-white"
                style={{ margin: "0 0 20px 0", padding: "2rem 0 3rem" }}
            >
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-4 col-md-6">
                            <div className="service-card service-card-3">
                                <div className="content">
                                    <h5 className="title">Kevesebb admin, több bevétel</h5>
                                    <ul style={{listStyle: "none"}}>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>
                                            Automatizált rendelés- és számlakezelés
                                        </li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>
                                            Webshop, POS és raktár összekapcsolva
                                        </li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>
                                            Automatikus értesítések ügyfeleknek és partnereknek
                                        </li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>
                                            NAV-kompatibilis bizonylatkezelés
                                        </li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>
                                            Teljesítmény- és bevételi statisztikák
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="service-card service-card-3">
                                <div className="content">
                                    <h5 className="title">Okos, egységes működés</h5>
                                    <ul style={{listStyle: "none"}}>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Integráció futárszolgáltatókkal (GLS, Foxpost, MPL, Wolt, stb.)</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Valós idejű készlet- és árkezelés</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Napi MNB árfolyamfrissítés</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>AI-támogatott beszerzés és előrejelzés</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Többnyelvű, többvalutás működés</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="service-card service-card-3">
                                <div className="content">
                                    <h5 className="title">Egyszerű bevezetés, skálázható</h5>
                                    <ul style={{listStyle: "none"}}>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Moduláris felépítés, gyors indulás</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Felhő-alapú és lokális telepítés is lehetséges</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Skálázható több telephelyre vagy franchise-rendszerre</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Magyar támogatás és oktatás</li>
                                        <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Biztonságos adatkezelés és jogosultság-kezelés</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Modules */}
            {/*<BlockHeader title={t('tradePage.modules.title')}/>*/}
            <div id="trade-modules" className="edu-about-area bg-image"
                 style={{margin: "0 0 20px 0", padding: "2rem 0 3rem"}}>
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-4 col-md-6">
                            <div className="inner">
                                <h4>{t('tradePage.modules.webshop.title')}</h4>
                                <ul className="mt--10" style={{listStyle: "none", padding: 0}}>
                                    <li><strong>Webshop</strong></li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Automatikus ár- és készletszinkron</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Megrendelés- és visszáru kezelés</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Fizetési és szállítási integrációk (GLS, MPL, Wolt, Foxpost, stb.)</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Akciók, kuponok és hűségprogramok</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>AI-alapú ajánlórendszer és keresőoptimalizálás</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Többnyelvű és többvalutás támogatás</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Webshop-analitika és konverziókövetés</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="inner">
                                <h4>{t('tradePage.modules.pos.title')}</h4>
                                <ul className="mt--10" style={{listStyle: "none", padding: 0}}>
                                    <li><strong>POS & pénztár</strong></li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Gyors értékesítés és számlázás</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Készpénz / kártya / utánvét támogatás</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Kassza, váltópénz és zárás-ellenőrzés</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>NAV / NTAK megfelelés és automatikus adatszolgáltatás</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Érintőképernyős és mobil POS mód</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Vonalkód- és mérlegrendszerek</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Vendégtér / asztalkezelés és nyugta-bontás</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="inner">
                                <h4>{t('tradePage.modules.b2b.title')}</h4>
                                <ul className="mt--10" style={{listStyle: "none", padding: 0}}>
                                    <li><strong>B2B értékesítés</strong></li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Partnerek, árlisták és szerződéses feltételek kezelése</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Egyedi kedvezmények és limitált akciók</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Ajánlat- és rendeléskezelés automatizmusokkal</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Beszerzési és értékesítési statisztikák</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>Export / import (CSV, XLSX, SAP, XML)</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>API integráció külső rendszerekkel</li>
                                    <li><i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>AI ajánlatgenerálás és partneri előrejelzés</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/*<DemoBanner/>*/}

            {/*/!* Integrations strip (infinite carousel) *!/*/}
            {/*<div id="trade-integrations" className="eduvibe-cta-area cta-style-7 bg-color-white"*/}
            {/*     style={{margin: '30px 0', padding: '2.5rem 0'}}>*/}
            {/*    <div className="container">*/}
            {/*        /!* Reusing global Integrations carousel component *!/*/}
            {/*        <IntegrationsCarousel/>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/*/!* Use cases *!/*/}
            {/*<BlockHeader title={t('tradePage.useCases.title')} description={t('tradePage.useCases.desc')}/>*/}
            {/*<div id="trade-use-cases" className="edu-about-area bg-image" style={{margin: '30px 0', padding: '3rem 0'}}>*/}
            {/*    <div className="container">*/}
            {/*        <div className="row g-5">*/}
            {/*            {[...useCases].map((uc, i) => (*/}
            {/*                <div key={`uc-${i + "-" + uc.title}`} className="col-lg-4 col-md-6">*/}
            {/*                    <div className="service-card service-card-3" style={{height: '100%'}}>*/}
            {/*                        <div className="content">*/}
            {/*                            <h5 className="title">{uc.title}</h5>*/}
            {/*                            <ul>*/}
            {/*                                {uc.points.map((p, k) => (*/}
            {/*                                    <li key={`uc-${i + "-" + uc.title}-${k}`}>*/}
            {/*                                        <i className="fas fa-check-square" aria-hidden="true" style={{color: "rgb(247, 140, 69)", marginRight: "6px"}}></i>*/}
            {/*                                        {p}*/}
            {/*                                    </li>))}*/}
            {/*                            </ul>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/*<hr/>*/}


            {/*<div id="trade-intro" className="edu-about-area about-style-3 bg-image"*/}
            {/*     style={{margin: "30px 0", padding: "4rem 0"}}>*/}
            {/*    <div className="container eduvibe-animated-shape">*/}
            {/*        <div className="row g-5 align-items-center">*/}
            {/*            <div className="col-lg-12">*/}
            {/*                <div className="section-title text-center">*/}
            {/*                    <h3 className="title">{t('tradePage.intro.title')}</h3>*/}
            {/*                    <p className="description mt--20" style={{maxWidth: 900, margin: "0 auto"}}>*/}
            {/*                        {t('tradePage.intro.desc')}*/}
            {/*                    </p>*/}
            {/*                </div>*/}
            {/*                <div className="col-lg-12"*/}
            {/*                     style={{alignItems: "center", display: "flex", justifyContent: "center"}}>*/}
            {/*                    <div className="col-lg-4" style={{textAlign: "right"}}>*/}
			{/*														<Demo title={t('erpPage.hero.ctaDemo')}/>*/}
            {/*                        /!* <button className="edu-btn" onClick={(e) => {*/}
            {/*                            e.preventDefault();*/}
            {/*                            if (window.openContactModal) window.openContactModal();*/}
            {/*                        }}>{t('tradePage.hero.ctaDemo')}</button> *!/*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/*<hr/>*/}

            {/*/!* Final CTA *!/*/}
            {/*<div id="trade-cta" className="eduvibe-cta-area cta-style-7 bg-image"*/}
            {/*     style={{margin: "30px 0", padding: "3rem 0 4rem"}}>*/}
            {/*    <div className="container">*/}
            {/*        <div className="row g-5 align-items-center">*/}
            {/*            <div className="col-lg-7">*/}
            {/*                <h3 className="title">{t('tradePage.finalCta.headline')}</h3>*/}
            {/*            </div>*/}
            {/*            <div className="col-lg-5" style={{display: "flex", gap: 12, justifyContent: "flex-end"}}>*/}
            {/*                <button className="edu-btn btn-secondary" onClick={(e) => {*/}
            {/*                    e.preventDefault();*/}
            {/*                    if (window.openContactModal) window.openContactModal();*/}
            {/*                }}>Ingyenes konzultáció</button>*/}
            {/*                <button className="edu-btn" onClick={() => openModal("quote")}>*/}
            {/*                    Ajánlat kérése*/}
            {/*                </button>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* POS-like info + animated widget */}
            {/*<div className="happy-pos edu-event-details-area edu-event-details edu-section-gap bg-color-white"*/}
            {/*     style={{overflowX: 'hidden'}}>*/}
            {/*    <div className="container">*/}
            {/*        <div className="row g-5">*/}
            {/*            <div className="col-lg-7">*/}
            {/*                <div className="content" style={{marginTop: 0}}>*/}
            {/*                    <h4 className="title" style={{fontSize: 30}}>{t('tradePage.more1.title')}</h4>*/}
            {/*                    <ul>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more1.b1')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more1.b2')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more1.b3')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>*/}
            {/*                            <a className="text-primary" href="/termekeink/happy-pos" target="_blank" rel="noreferrer">*/}
            {/*                                {t('tradePage.more1.mobileOrder')}*/}
            {/*                            </a>*/}
            {/*                            {t('tradePage.more1.mobileOrderTail')}*/}
            {/*                        </li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more1.b5')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>*/}
            {/*                            {t('tradePage.more1.ntakPre')}*/}
            {/*                            <a className="text-primary" href="https://ntak.hu/" target="_blank" rel="noreferrer">*/}
            {/*                                {t('tradePage.more1.ntakLink')}*/}
            {/*                            </a>*/}
            {/*                            {t('tradePage.more1.ntakPost')}*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>*/}
            {/*                <div className="content" style={{marginTop: 0}}>*/}
            {/*                    <h4 className="title" style={{fontSize: 30}}>{t('tradePage.more2.title')}</h4>*/}
            {/*                    <ul>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b1')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b2')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b3')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b4')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b5')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b6')}</li>*/}
            {/*                        <li style={{fontSize: 17}}>{t('tradePage.more2.b7')}</li>*/}
            {/*                    </ul>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*            <div className="col-lg-5">*/}
            {/*                <RealtimeSyncWidget/>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Modern Modal */}
            {showModal && (
                <div
                    onClick={closeModal}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1050,
                        background: 'radial-gradient(80% 120% at 10% 10%, rgba(27,74,232,0.25) 0%, rgba(10,20,40,0.65) 50%, rgba(5,10,20,0.8) 100%)',
                        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                    }}
                    aria-hidden="true"
                >
                    <div
                        role="dialog" aria-modal="true" aria-labelledby="trade-modal-title"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 'min(980px, 96vw)',
                            maxHeight: '92vh',
                            overflow: 'auto',
                            borderRadius: 16,
                            background: '#ffffff',
                            boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
                            position: 'relative'
                        }}
                    >
                        {/* Decorative gradient strip */}
                        <div style={{position: 'absolute', inset: 0, pointerEvents: 'none'}}>
                            <div style={{
                                position: 'absolute',
                                top: -60,
                                right: -40,
                                width: 220,
                                height: 220,
                                background: 'radial-gradient(circle, rgba(27,74,232,0.15), transparent 70%)',
                                filter: 'blur(8px)'
                            }}/>
                            <div style={{
                                position: 'absolute',
                                bottom: -80,
                                left: -60,
                                width: 260,
                                height: 260,
                                background: 'radial-gradient(circle, rgba(124,77,255,0.12), transparent 70%)',
                                filter: 'blur(10px)'
                            }}/>
                        </div>

                        <div style={{
                            padding: '18px 22px',
                            borderBottom: '1px solid #eef1f6',
                            background: 'linear-gradient(135deg, #f7faff 0%, #ffffff 60%)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                <div style={{
                                    padding: '6px 10px',
                                    borderRadius: 999,
                                    background: '#eef3ff',
                                    color: '#1b4ae8',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    letterSpacing: 0.3
                                }}>
                                    {formType === 'consult' ? 'Ingyenes konzultáció' : formType === 'demo' ? 'Bemutató igénylése' : 'Árajánlatkérés'}
                                </div>
                                <h3 id="trade-modal-title" style={{margin: 0, fontSize: 20}}>Mesélj pár dologról — 1
                                    perc az egész</h3>
                                <div style={{fontSize: 12, color: '#5b6574'}}>1 perc alatt kész • Nincs kötelezettség
                                </div>
                                <span style={{flex: 1}}/>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    aria-label="Bezárás"
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 999,
                                        border: '1px solid #e7eaf3',
                                        background: '#fff',
                                        display: 'grid',
                                        placeItems: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div style={{padding: 22}}>
                            {result ? (
                                <div className="text-center" role="alert" style={{padding: 24}}>
                                    <h4 className="alert-heading"
                                        style={{fontSize: '1.8rem', marginBottom: 8}}>Köszönjük a megkeresést!</h4>
                                    <p style={{marginBottom: 12, color: '#1b2b5b'}}>1 munkanapon belül felvesszük veled
                                        a kapcsolatot.</p>
                                    <button className="rn-btn edu-btn w-100 mt--10" onClick={closeModal}>Bezárás
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    {submitError && (
                                        <div role="alert" style={{
                                            background: '#fff4f4',
                                            border: '1px solid #ffd6d6',
                                            color: '#8a1f1f',
                                            borderRadius: 8,
                                            padding: 12,
                                            marginBottom: 12
                                        }}>
                                            {submitError}
                                        </div>
                                    )}
                                    <div className="row g-3">
                                        <div className="col-lg-6">
                                            <label className="form-label">Név (opcionális)</label>
                                            <input type="text" className="form-control" name="fullname"
                                                   placeholder="Pl.: Kovács Anna"/>
                                        </div>

                                        <div className="col-lg-6" style={{position: 'relative'}}>
                                            <label className="form-label">Cég neve (keresés)</label>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                marginBottom: 6
                                            }}>
                                                <input id="isPrivate" type="checkbox" checked={isPrivate}
                                                       onChange={(e) => {
                                                           setIsPrivate(e.target.checked);
                                                           setCompanyQuery('');
                                                           setSelectedCompany(null);
                                                           setShowCompanyDropdown(false);
                                                       }}/>
                                                <label htmlFor="isPrivate" style={{
                                                    margin: 0,
                                                    fontSize: 13,
                                                    color: '#5b6574',
                                                    cursor: 'pointer'
                                                }}>Magánszemély vagyok / nincs cég</label>
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="company"
                                                placeholder={isPrivate ? 'Magánszemélyként jelentkezem' : 'Kezdje el gépelni a cég nevét...'}
                                                value={companyQuery}
                                                onChange={(e) => {
                                                    setCompanyQuery(e.target.value);
                                                    setSelectedCompany(null);
                                                }}
                                                onFocus={() => setShowCompanyDropdown(!isPrivate && companyResults.length > 0)}
                                                autoComplete="off"
                                                disabled={isPrivate}
                                                aria-disabled={isPrivate}
                                            />
                                            {!isPrivate && companyLoading && (
                                                <div style={{
                                                    position: 'absolute',
                                                    right: 10,
                                                    top: 40,
                                                    color: '#1b4ae8',
                                                    fontSize: 12
                                                }}>Keresés...</div>
                                            )}
                                            {!isPrivate && companyError && (
                                                <div style={{
                                                    fontSize: 12,
                                                    color: '#c00',
                                                    marginTop: 6
                                                }}>{companyError}</div>
                                            )}
                                            {!isPrivate && showCompanyDropdown && companyResults?.length > 0 && (
                                                <div style={{
                                                    position: 'absolute',
                                                    zIndex: 3,
                                                    top: 102,
                                                    left: 0,
                                                    right: 0,
                                                    maxHeight: 260,
                                                    overflow: 'auto',
                                                    border: '1px solid #e7eaf3',
                                                    borderRadius: 12,
                                                    background: '#fff',
                                                    boxShadow: '0 12px 30px rgba(0,0,0,0.12)'
                                                }} role="listbox">
                                                    {companyResults.slice(0, 8).map((it, idx) => {
                                                        const name = it?.name || it?.cegnev || it?.companyName || it?.nev || '';
                                                        const tax = it?.adoszam || it?.tax || it?.taxNumber || it?.vat || '';
                                                        const address = it?.address || it?.cim || it?.fullAddress || '';
                                                        return (
                                                            <div
                                                                key={`co-${idx}-${name}`}
                                                                onClick={() => {
                                                                    setSelectedCompany(it);
                                                                    setCompanyQuery(name);
                                                                    setShowCompanyDropdown(false);
                                                                }}
                                                                style={{
                                                                    padding: '10px 12px',
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #f2f4f8'
                                                                }}
                                                                role="option"
                                                                aria-selected={false}
                                                            >
                                                                <div
                                                                    style={{fontWeight: 600, fontSize: 14}}>{name}</div>
                                                                <div style={{
                                                                    fontSize: 12,
                                                                    color: '#5b6574'
                                                                }}>{tax ? `Adószám: ${tax}` : address}</div>
                                                            </div>
                                                        );
                                                    })}
                                                    <div style={{padding: '8px 12px', textAlign: 'right'}}>
                                                        <button type="button"
                                                                onClick={() => setShowCompanyDropdown(false)} style={{
                                                            fontSize: 12,
                                                            color: '#5b6574',
                                                            background: 'transparent',
                                                            border: 'none',
                                                            textDecoration: 'underline',
                                                            cursor: 'pointer'
                                                        }}>Nem találom a cégem
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {selectedCompany && !isPrivate && (
                                                <div style={{marginTop: 6, fontSize: 12, color: '#1b4ae8'}}>
                                                    Kiválasztva: {selectedCompany.name || selectedCompany.cegnev || selectedCompany.companyName || selectedCompany.nev}
                                                    <button type="button" onClick={() => {
                                                        setSelectedCompany(null);
                                                        setCompanyQuery('');
                                                    }} style={{
                                                        marginLeft: 8,
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: '#5b6574',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer'
                                                    }}>Mégse
                                                    </button>
                                                </div>
                                            )}
                                            {!isPrivate && <small className="text-muted" style={{
                                                display: 'block',
                                                marginTop: 6,
                                                opacity: 0.8
                                            }}>Adatforrás: HappyGastro cégkereső API</small>}
                                        </div>

                                        <div className="col-lg-6">
                                            <label className="form-label">Email*</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                placeholder="pl. valaki@ceg.hu"
                                                required
                                                autoFocus
                                                value={emailVal}
                                                onChange={(e) => setEmailVal(e.target.value)}
                                                onBlur={() => setEmailTouched(true)}
                                                aria-invalid={emailTouched && !emailValid}
                                                aria-describedby="email-help"
                                            />
                                            <small id="email-help" style={{
                                                display: 'block',
                                                marginTop: 6,
                                                color: (emailTouched && !emailValid) ? '#c00' : '#5b6574',
                                                fontSize: 12
                                            }}>
                                                {emailTouched && !emailValid ? 'Kérjük, érvényes email címet adj meg.' : 'Ide küldjük a visszaigazolást.'}
                                            </small>
                                        </div>
                                        <div className="col-lg-6">
                                            <label className="form-label">Telefonszám*</label>
                                            <InputMask
                                                className="form-control"
                                                autoComplete="new-password"
                                                name="phone"
                                                id="trade-quote-phone"
                                                mask="+36 (99) 999-9999"
                                                placeholder="+36 (30) 123-4567"
                                                required
                                                value={phoneVal}
                                                onChange={(e) => setPhoneVal(e.target.value)}
                                                onBlur={() => setPhoneTouched(true)}
                                                aria-invalid={phoneTouched && !phoneValid}
                                                aria-describedby="phone-help"
                                            />
                                            <small id="phone-help" style={{
                                                display: 'block',
                                                marginTop: 6,
                                                color: (phoneTouched && !phoneValid) ? '#c00' : '#5b6574',
                                                fontSize: 12
                                            }}>
                                                {phoneTouched && !phoneValid ? 'Kérjük, érvényes telefonszámot adj meg.' : 'Példa: +36 (30) 123-4567'}
                                            </small>
                                        </div>

                                        <div className="col-lg-12">
                                            <hr/>
                                        </div>

                                        <div className="col-lg-12">
                                            <label className="form-label">1. Milyen webáruháza van?</label>
                                            <input type="text" className="form-control" name="webshopType"
                                                   placeholder="Pl.: Shopify, WooCommerce, egyedi, stb."
                                                   ref={webshopTypeRef}/>
                                            <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8}}>
                                                {['Shoprenter', 'WooCommerce', 'Shopify', 'Egyedi', 'Egyéb'].map((opt) => (
                                                    <button key={opt} type="button" onClick={() => {
                                                        if (webshopTypeRef?.current) {
                                                            webshopTypeRef.current.value = opt === 'Egyéb' ? '' : opt;
                                                            webshopTypeRef.current.focus();
                                                        }
                                                    }}
                                                            style={{
                                                                padding: '6px 10px',
                                                                borderRadius: 999,
                                                                border: '1px solid #e7eaf3',
                                                                background: '#f7f9ff',
                                                                color: '#1b2b5b',
                                                                fontSize: 12,
                                                                cursor: 'pointer'
                                                            }}>
                                                        {opt}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <label className="form-label">2. Van-e boltja?</label>
                                            <select className="form-control" name="hasStore" defaultValue="no"
                                                    ref={hasStoreSelectRef}
                                                    onChange={(e) => setHasStoreVal(e.target.value)}>
                                                <option value="no">Nincs</option>
                                                <option value="yes">Van</option>
                                            </select>
                                            <div style={{marginTop: 8, display: 'flex', gap: 8}}>
                                                <button type="button" onClick={() => {
                                                    if (hasStoreSelectRef?.current) {
                                                        hasStoreSelectRef.current.value = 'no';
                                                        setHasStoreVal('no');
                                                    }
                                                }}
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderRadius: 999,
                                                            border: '1px solid #e7eaf3',
                                                            background: hasStoreVal === 'no' ? '#eaf2ff' : '#f7f9ff',
                                                            color: '#1b2b5b',
                                                            fontSize: 12,
                                                            cursor: 'pointer'
                                                        }}>Nincs
                                                </button>
                                                <button type="button" onClick={() => {
                                                    if (hasStoreSelectRef?.current) {
                                                        hasStoreSelectRef.current.value = 'yes';
                                                        setHasStoreVal('yes');
                                                        setTimeout(() => storeCountInputRef?.current?.focus(), 0);
                                                    }
                                                }}
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderRadius: 999,
                                                            border: '1px solid #e7eaf3',
                                                            background: hasStoreVal === 'yes' ? '#eaf2ff' : '#f7f9ff',
                                                            color: '#1b2b5b',
                                                            fontSize: 12,
                                                            cursor: 'pointer'
                                                        }}>Van
                                                </button>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <label className="form-label">Ha igen, hány?</label>
                                            <input type="number" className="form-control" name="storeCount" min="0"
                                                   defaultValue="0" ref={storeCountInputRef}
                                                   disabled={hasStoreVal !== 'yes'}/>
                                        </div>
                                        <div className="col-lg-3">
                                            <label className="form-label">Tervezés alatt (db)</label>
                                            <input type="number" className="form-control" name="plannedStores" min="0"
                                                   defaultValue="0"/>
                                        </div>
                                        <div className="col-lg-4">
                                            <label className="form-label">3/a. Hány webshopja van?</label>
                                            <input type="number" className="form-control" name="shopCount" min="0"
                                                   defaultValue="1"/>
                                        </div>
                                        <div className="col-lg-4">
                                            <label className="form-label">3/b. Havi online rendelések száma?</label>
                                            <input type="number" className="form-control" name="monthlyOrders" min="0"
                                                   defaultValue="0" ref={monthlyOrdersRef}/>
                                            <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6}}>
                                                {[
                                                    {label: '0–100', value: 100},
                                                    {label: '100–500', value: 500},
                                                    {label: '500–2000', value: 2000},
                                                    {label: '2000+', value: 2500},
                                                ].map((o) => (
                                                    <button key={o.label} type="button" onClick={() => {
                                                        if (monthlyOrdersRef?.current) {
                                                            monthlyOrdersRef.current.value = o.value;
                                                            monthlyOrdersRef.current.focus();
                                                        }
                                                    }}
                                                            style={{
                                                                padding: '4px 8px',
                                                                borderRadius: 999,
                                                                border: '1px solid #e7eaf3',
                                                                background: '#f7f9ff',
                                                                fontSize: 12,
                                                                cursor: 'pointer'
                                                            }}>
                                                        {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-lg-4">
                                            <label className="form-label">3/c. Felhasználók száma?</label>
                                            <input type="number" className="form-control" name="userCount" min="1"
                                                   defaultValue="1"/>
                                        </div>

                                        <div className="col-lg-12 mt--10">
                                            <button className="rn-btn edu-btn w-100" type="submit"
                                                    disabled={!emailValid || !phoneValid || submitting}>
                                                <span>{submitting ? 'Küldés...' : 'Űrlap beküldése'}</span>
                                                <i className="icon-arrow-right-line-right"></i>
                                            </button>
                                        </div>

                                        <div className="col-lg-12" style={{marginTop: 12}}>
                                            <div style={{fontSize: 12, color: '#5b6574'}}>
                                                A “Küldés” gombra kattintva elfogadod az adatkezelési tájékoztatót és
                                                hozzájárulsz, hogy felvegyük veled a kapcsolatot.
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
        <IntegrationsCarousel/>
        <FooterOne/>
    </>
  );
};

export default TradePageContent;
